import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { portant } from '../../theme/portantTheme';

type Flow = 'tag' | 'signature';

interface Step {
  sel: string;
  cap: string;
}

const STEPS: Record<Flow, Step[]> = {
  tag: [
    { sel: '.ihint-caret', cap: 'Click where the tag should go' },
    { sel: '.ihint-insert', cap: 'Click Insert' },
    { sel: '.ihint-doctag', cap: 'The tag is added to your document' },
  ],
  signature: [
    { sel: '.ihint-copy', cap: 'Click Copy on the field' },
    { sel: '.ihint-paste', cap: 'Click into your document' },
    { sel: '.ihint-sigph', cap: 'Paste, and the signer field appears' },
  ],
};

const STORE_PREFIX = 'portant-addin-hint-';
const SIG_IMG = 'https://static.portant.co/field-signature-u1.png';
const STEP_MS = 2200;

// Palette kept local so the illustration is self-contained; blue tracks the
// brand accent used elsewhere in the pane.
const CSS = `
.ihint-root { --a: ${portant.blue || '#2b5fd0'}; --aink: #ffffff; --asoft: #e9f0fe; --aline: #cdddff;
  --ink: #1b1815; --soft: #56504a; --faint: #8b847b; --line: #e6e1da; --linestrong: #d7d0c5;
  --surface: #ffffff; --surface2: #faf9f7; }
.ihint-stage { position: relative; width: 100%; max-width: 340px; height: 208px; margin: 0 auto;
  background: var(--surface2); border: 1px solid var(--line); border-radius: 10px; overflow: hidden; }
.ihint-camera { position: absolute; top: 0; left: 0; height: 100%; display: flex; align-items: stretch;
  gap: 12px; padding: 12px; box-sizing: border-box; transition: transform .7s cubic-bezier(.5,.05,.2,1); }
.ihint-root[data-flow="tag"] .ihint-camera { width: 100%; }
.ihint-root[data-flow="signature"] .ihint-camera { width: auto; }
.ihint-doc { background: #fff; border: 1px solid var(--line); border-radius: 9px; padding: 13px; overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,.05); }
.ihint-root[data-flow="tag"] .ihint-doc { flex: 1; }
.ihint-root[data-flow="signature"] .ihint-doc { flex: none; width: 280px; }
.ihint-pane { width: 168px; flex: none; align-self: flex-start; max-height: 100%; overflow: hidden; background: var(--surface);
  border: 1px solid var(--line); border-radius: 9px; padding: 8px; display: flex; flex-direction: column; gap: 6px;
  box-shadow: 0 1px 2px rgba(0,0,0,.05); }
.ihint-head { display: flex; align-items: center; gap: 6px; font-size: 10px; font-weight: 650; }
.ihint-dot { width: 13px; height: 13px; border-radius: 4px; background: var(--a); flex: none; }
.ihint-tabs { display: flex; gap: 4px; }
.ihint-tab { font-size: 8.5px; padding: 3px 6px; border-radius: 5px; color: var(--faint); background: var(--surface2); }
.ihint-tab.on { color: var(--a); background: var(--asoft); font-weight: 650; }
.ihint-accw { font-size: 9.5px; font-weight: 650; background: var(--surface2); border: 1px solid var(--line);
  border-radius: 6px; padding: 4px 7px; display: flex; justify-content: space-between; }
.ihint-rows { display: flex; flex-direction: column; gap: 4px; }
.ihint-row { display: flex; align-items: center; gap: 6px; padding: 4px 6px; border: 1px solid var(--line);
  border-radius: 6px; background: var(--surface); }
.ihint-row.active { border-color: var(--aline); background: var(--asoft); }
.ihint-meta { flex: 1; min-width: 0; }
.ihint-name { font-size: 9.5px; font-weight: 650; }
.ihint-sub { font-size: 8px; color: var(--faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ihint-btn { position: relative; font-size: 8.5px; font-weight: 650; border-radius: 5px; padding: 4px 8px;
  border: 1px solid var(--a); color: var(--a); background: var(--surface); white-space: nowrap;
  transition: transform .2s ease, background .2s ease, color .2s ease; }
.ihint-btn.ghost { border-color: var(--linestrong); color: var(--faint); }
.ihint-lblb { display: none; }
.ihint-line { height: 6px; border-radius: 3px; background: var(--line); margin: 0 0 8px; }
.ihint-line.s { width: 45%; } .ihint-line.m { width: 70%; }
.ihint-para { font-size: 11px; color: var(--ink); margin: 2px 0 10px; display: flex; align-items: center; flex-wrap: wrap; }
.ihint-caret { display: inline-block; width: 1.5px; height: 13px; background: var(--a); vertical-align: middle; animation: ihint-blink 1.1s steps(1) infinite; }
.ihint-doctag { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 10.5px; color: var(--a);
  background: var(--asoft); border-radius: 4px; padding: 0 3px; opacity: 0; transform: translateY(4px);
  transition: opacity .4s ease, transform .4s ease; }
.ihint-siglabel { font-size: 10.5px; color: var(--soft); margin: 8px 0 6px; }
.ihint-sigline { display: flex; align-items: center; gap: 3px; min-height: 46px; }
.ihint-paste { display: inline-block; width: 1.5px; height: 14px; background: var(--a); opacity: 0; }
.ihint-sigph { width: 150px; height: auto; opacity: 0; transform: translateY(5px) scale(.98);
  transition: opacity .45s ease, transform .45s ease; }
.ihint-toast { position: absolute; left: 14px; bottom: 12px; padding: 4px 9px; border-radius: 999px;
  background: var(--ink); color: #fff; font-size: 9px; font-weight: 650; opacity: 0; transform: translateY(4px);
  transition: opacity .3s ease, transform .3s ease; pointer-events: none; }
.ihint-cursor { position: absolute; top: 0; left: 0; pointer-events: none; z-index: 5;
  transform: translate(var(--x, 20px), var(--y, 180px)); }
.ihint-cursor svg { display: block; filter: drop-shadow(0 1px 2px rgba(0,0,0,.35)); }

/* step-driven reveals */
.ihint-stage[data-step="3"] .ihint-doctag { opacity: 1; transform: none; }
.ihint-stage[data-step="3"] .ihint-sigph { opacity: 1; transform: none; }
.ihint-stage[data-step="2"] .ihint-paste, .ihint-stage[data-step="3"] .ihint-paste { opacity: 1; }
.ihint-stage[data-step="2"] .ihint-insert, .ihint-stage[data-step="3"] .ihint-insert { transform: scale(.93); background: var(--a); color: var(--aink); }
.ihint-stage[data-step="1"] .ihint-copy { transform: scale(.93); background: var(--a); color: var(--aink); }
.ihint-stage[data-step="3"] .ihint-insert .ihint-lbla { display: none; }
.ihint-stage[data-step="3"] .ihint-insert .ihint-lblb { display: inline; }
.ihint-root[data-flow="signature"] .ihint-stage[data-step="1"] .ihint-toast { opacity: 1; transform: none; }

.ihint-stepper { list-style: none; margin: 12px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.ihint-stepper li { display: flex; align-items: center; gap: 9px; font-size: 12px; color: var(--faint); transition: color .3s ease; }
.ihint-sn { flex: none; box-sizing: border-box; width: 20px; height: 20px; border-radius: 50%; border: 1.5px solid var(--linestrong);
  color: var(--faint); font-size: 10.5px; font-weight: 700; line-height: 1; display: flex; align-items: center; justify-content: center;
  transition: background .3s ease, border-color .3s ease, color .3s ease; }
.ihint-stepper li.done .ihint-sn { border-color: var(--a); color: var(--a); }
.ihint-stepper li.active { color: var(--ink); font-weight: 600; }
.ihint-stepper li.active .ihint-sn { background: var(--a); border-color: var(--a); color: var(--aink); }

@keyframes ihint-blink { 50% { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .ihint-caret { animation: none; } }
`;

const CursorSvg: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24">
    <path d="M4 2 L4 18 L8.5 13.5 L11.5 20 L14 19 L11 12.5 L17 12.5 Z" fill="#fff" stroke="#1b1815" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

const TagStage: React.FC = () => (
  <div className="ihint-doc">
    <div className="ihint-line s"></div>
    <div className="ihint-para">Hi&nbsp;<span className="ihint-caret"></span><span className="ihint-doctag">{'{{First name}}'}</span>,</div>
    <div className="ihint-line"></div>
    <div className="ihint-line m"></div>
    <div className="ihint-line"></div>
  </div>
);

const TagPane: React.FC = () => (
  <div className="ihint-pane">
    <div className="ihint-head"><span className="ihint-dot"></span>Invoice workflow</div>
    <div className="ihint-tabs"><span className="ihint-tab on">Merge tags</span><span className="ihint-tab">Signatures</span></div>
    <div className="ihint-rows">
      <div className="ihint-row active">
        <div className="ihint-meta"><div className="ihint-name">First name</div><div className="ihint-sub">{'{{First name}}'}</div></div>
        <button className="ihint-btn ihint-insert" type="button"><span className="ihint-lbla">Insert</span><span className="ihint-lblb">Inserted</span></button>
      </div>
      <div className="ihint-row">
        <div className="ihint-meta"><div className="ihint-name">Company</div><div className="ihint-sub">{'{{Company}}'}</div></div>
        <button className="ihint-btn ghost" type="button">Insert</button>
      </div>
    </div>
  </div>
);

const SigStage: React.FC = () => (
  <div className="ihint-doc">
    <div className="ihint-line s"></div>
    <div className="ihint-line"></div>
    <div className="ihint-siglabel">Signed by recipient:</div>
    <div className="ihint-sigline"><span className="ihint-paste"></span><img className="ihint-sigph" src={SIG_IMG} alt="Signature field" /></div>
    <div className="ihint-line m"></div>
  </div>
);

const SigPane: React.FC = () => (
  <div className="ihint-pane">
    <div className="ihint-head"><span className="ihint-dot"></span>Contract workflow</div>
    <div className="ihint-tabs"><span className="ihint-tab">Merge tags</span><span className="ihint-tab on">Signatures</span></div>
    <div className="ihint-accw"><span>Recipient 1</span><span>&#9662;</span></div>
    <div className="ihint-rows">
      <div className="ihint-row active">
        <div className="ihint-meta"><div className="ihint-name">Signature</div><div className="ihint-sub">Sign here</div></div>
        <button className="ihint-btn ihint-copy" type="button">Copy</button>
      </div>
      <div className="ihint-row">
        <div className="ihint-meta"><div className="ihint-name">Date</div><div className="ihint-sub">Auto-stamped</div></div>
        <button className="ihint-btn ghost" type="button">Copy</button>
      </div>
    </div>
  </div>
);

interface Props {
  flow: Flow;
}

const InsertHint: React.FC<Props> = ({ flow }) => {
  const key = STORE_PREFIX + flow;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  });
  const [step, setStep] = useState(0);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const stepRef = useRef(0);
  const steps = STEPS[flow];

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(key, next ? '1' : '0');
      } catch (e) {
        /* ignore */
      }
      return next;
    });
  };

  // Reflect the active step into the stage (CSS reveals) and slide the camera.
  useEffect(() => {
    stepRef.current = step;
    const stage = stageRef.current;
    if (!stage) return;
    stage.dataset.step = String(step + 1);
    if (flow === 'signature') {
      const cam = stage.querySelector('.ihint-camera') as HTMLElement | null;
      if (cam) {
        const shift = Math.max(0, cam.offsetWidth - stage.clientWidth);
        cam.style.transform = 'translateX(' + (step === 0 ? -shift : 0) + 'px)';
      }
    }
  }, [step, flow, collapsed]);

  // Drive the animation: a smooth cursor follow plus a stepping timer. Skipped
  // when collapsed or when the user prefers reduced motion.
  useEffect(() => {
    if (collapsed) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stage = stageRef.current;
    if (!stage) return undefined;

    const cur = { x: null as number | null, y: null as number | null };
    const measure = () => {
      const stg = stageRef.current;
      if (!stg || stg.offsetParent === null) return null;
      const el = stg.querySelector(steps[stepRef.current].sel) as HTMLElement | null;
      if (!el) return null;
      const s = stg.getBoundingClientRect();
      const t = el.getBoundingClientRect();
      return { x: t.left - s.left + t.width / 2, y: t.top - s.top + t.height / 2 };
    };

    if (reduce) {
      setStep(steps.length - 1);
      const id = requestAnimationFrame(() => {
        const m = measure();
        if (m && stageRef.current) {
          stageRef.current.style.setProperty('--x', m.x + 'px');
          stageRef.current.style.setProperty('--y', m.y + 'px');
        }
      });
      return () => cancelAnimationFrame(id);
    }

    setStep(0);
    let raf = 0;
    const loop = () => {
      const m = measure();
      if (m && stageRef.current) {
        if (cur.x === null) {
          cur.x = m.x;
          cur.y = m.y;
        }
        cur.x += (m.x - cur.x) * 0.055;
        cur.y += (m.y - (cur.y as number)) * 0.055;
        stageRef.current.style.setProperty('--x', cur.x + 'px');
        stageRef.current.style.setProperty('--y', (cur.y as number) + 'px');
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    let tick = 0;
    const timer = window.setInterval(() => {
      tick = (tick + 1) % (steps.length + 1);
      setStep(tick >= steps.length ? steps.length - 1 : tick);
    }, STEP_MS);

    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(timer);
    };
  }, [collapsed, flow]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box className="ihint-root" data-flow={flow} sx={{ m: 2, mb: 1.5 }}>
      <style>{CSS}</style>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="overline" sx={{ color: portant.orange }}>How it works</Typography>
        <Button
          size="small"
          onClick={toggle}
          sx={{ fontSize: '11px', textTransform: 'none', color: portant.charred, minWidth: 0 }}
        >
          {collapsed ? 'Show me how' : 'Hide'}
        </Button>
      </Stack>

      {!collapsed && (
        <>
          <div className="ihint-stage" ref={stageRef} data-step="1">
            <div className="ihint-camera">
              {flow === 'tag' ? (
                <>
                  <TagStage />
                  <TagPane />
                </>
              ) : (
                <>
                  <SigStage />
                  <SigPane />
                </>
              )}
            </div>
            <div className="ihint-cursor"><CursorSvg /></div>
            {flow === 'signature' && <div className="ihint-toast">Copied</div>}
          </div>

          <ol className="ihint-stepper">
            {steps.map((s, i) => (
              <li key={i} className={i === step ? 'active' : i < step ? 'done' : ''}>
                <span className="ihint-sn">{i + 1}</span>
                <span>{s.cap}</span>
              </li>
            ))}
          </ol>
        </>
      )}
    </Box>
  );
};

export default InsertHint;
